import type { ChangeEvent, FunctionComponent } from 'react'

import { FlowPage } from '@monthio/design-system'
import {
	BudgetHelperEmailLogo,
	BudgetHelperEmailSentLogo,
} from '@monthio/icons'
import {
	EmailInputField,
	InputLabel,
	notificationService,
} from '@monthio/react-components'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { useBudgetConsumerEmailModule } from './useBudgetConsumerEmailModule'

type ViewState = 'form' | 'sent'

const ContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const EmailWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`

const StyledEmailField = styled(EmailInputField)`
	.MuiSvgIcon-root {
		font-size: 16px !important;
	}
`

export const BudgetConsumerEmail: FunctionComponent = () => {
	const { t, i18n } = useTranslation()
	const { config, finish } = useBudgetConsumerEmailModule()

	const hasFinishedRef = useRef(false)

	useEffect(() => {
		if (!config.isFinalFlow && !hasFinishedRef.current) {
			hasFinishedRef.current = true
			finish()
		}
	}, [config.isFinalFlow, finish])

	const [viewState, setViewState] = useState<ViewState>('form')
	const [isEmailValid, setIsEmailValid] = useState(false)
	const [email, setEmail] = useState<string>()

	const handleSendButtonClick = useCallback(async () => {
		try {
			if (!email) {
				throw new Error('email is undefined')
			}

			await config.sendBudgetHelperPdf({
				email,
				language: i18n.language,
			})

			setViewState('sent')
		} catch {
			notificationService.error(t('budgetHelper-emailPage-sendEmailError'))
		}
	}, [email, config, i18n.language, t])

	const handleEmailChange = useCallback(
		(ev: ChangeEvent<HTMLInputElement>, isValidated: boolean) => {
			setIsEmailValid(isValidated)
			if (isValidated) {
				setEmail(ev.target.value)
			}
		},
		[],
	)

	if (viewState === 'sent') {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<FlowPage.Header
						title={t('budgetHelper-emailSentPage-headerTitle')}
						description={t('budgetHelper-emailSentPage-headerSubtitle')}
						slot={<BudgetHelperEmailSentLogo />}
					/>
				</FlowPage.Content>
			</FlowPage>
		)
	}

	return (
		<FlowPage>
			<FlowPage.Content $spacing="xl">
				<FlowPage.Header
					slot={<BudgetHelperEmailLogo />}
					title={t('budgetHelper-emailPage-headerTitle')}
					description={t('budgetHelper-emailPage-headerSubtitle')}
				/>
				<ContentWrapper>
					<EmailWrapper>
						<InputLabel>{t('budgetHelper-emailPage-emailLabel')}</InputLabel>
						<StyledEmailField
							variant="outlined"
							size="small"
							onEmailChange={handleEmailChange}
						/>
					</EmailWrapper>
				</ContentWrapper>
			</FlowPage.Content>
			<FlowPage.Footer
				actions={
					<FlowPage.Cta
						disabled={!isEmailValid}
						onClick={handleSendButtonClick}
					>
						{t('budgetHelper-emailPage-sendPdfButton')}
					</FlowPage.Cta>
				}
			/>
		</FlowPage>
	)
}
